/**
 * rowLabels — the Outreach column's whole vocabulary as one pure mapping (owner sign-off 2026-10-15): a row shows ONE state
 * pill, at most ONE red badge when the admin must act, and ONE muted "next step / why not" line. Display only: statuses and
 * the API are untouched, and every input is a field the row already carries.
 */
import type { OutreachMessageStatus, OutreachRow, OutreachState, OutreachWaitingSend } from '@discovr/types';
import { isWithBrandStatus } from '@discovr/types';
import { zoneAbbrev } from '../../../lib/timeZones';
import { agreedAmountLabel } from './brandPill';
import { followupPauseAction } from './followupPauseUi';
import { isApprovedWaiting, isCreatorTold } from './handoffStates';
import { INTENT_LABELS } from './IntentPill';
import { SCHEDULED_WAITING_LABEL, heldReasonLabel } from './OutreachMessageStatusPill';
import { AWAITING_REPLY_HINT, AWAITING_REPLY_LABEL, escalationLabel, escalationTitle, followupDeferredLabel, followupLabel, isSequenceEnded, SEQUENCE_ENDED_LABEL } from './rowStateLabels';
import { shortDate, shortTime, zoneDayKey } from './shortDate';

export type PillTone = 'neutral' | 'brand' | 'replied' | 'good' | 'stopped' | 'removed' | 'stuck';
export interface RowPill { label: string; tone: PillTone; /** Hover text; the Stopped pill's reason lives here only. */ hint?: string; /** Why a Stopped row stopped, for the thread header to name beside the pill. */ stopReason?: string }
export interface RowBadge { label: string; hint?: string }
export interface RowLine { text: string; hint?: string }
export interface RowLabels { pill: RowPill; badge: RowBadge | null; doNotContact: RowBadge | null; line: RowLine | null }
export interface RowLabelContext { campaignZone?: string; autoFollowups?: boolean; now?: Date }

/** Owner 2026-10-15: an approved draft that waits for an email says what it will do, in the campaign zone. A chosen time that
 *  has passed goes out as soon as pacing and the cap allow, so it never reads as a date; no readable time claims none. */
export function waitingSendHint(choice: OutreachWaitingSend, zone: string | undefined, now: Date = new Date()): string {
  if (choice.mode === 'now') return 'Approved to send now. It goes out as soon as an email is added.';
  const at = choice.scheduledFor ? new Date(choice.scheduledFor) : null;
  if (!at || Number.isNaN(at.getTime())) return 'Approved. It goes out once an email is added.';
  if (at.getTime() <= now.getTime()) return 'The time you chose has passed. It goes out as soon as an email is added.';
  const when = zone ? `${shortDate(at, zone)}, ${shortTime(at, zone)} ${zoneAbbrev(zone, at)}` : `${shortDate(at)}, ${shortTime(at)}`;
  return `Approved for ${when}. It goes out once an email is added.`;
}

const ACTIVE_STATES: ReadonlySet<OutreachState> = new Set<OutreachState>(['sourced', 'contacted', 'replied', 'rate_provided']);
const STOPPED_REASONS = { rejected: 'Rejected', not_interested: 'Not interested', ghosted: 'Ghosted', bounced: 'Bounced' } as const;
const UNSUBSCRIBED_REASON = 'Unsubscribed';
export const CONTACTED_PILL_LABEL = 'Contacted';
export const UNKNOWN_PILL_LABEL = 'Unknown status';
export const SYNCING_PILL_LABEL = 'Syncing…';
export const SYNC_STUCK_PILL_LABEL = 'Sync stuck';
export const SYNC_STUCK_HINT = 'This row stopped updating. Ask an engineer to check it.';
const SCOPE_WORDS = { global: 'all Discovr outreach', brand: 'this brand', campaign: 'this campaign' } as const;
const SCOPE_TITLES = { global: 'Unsubscribed from all Discovr outreach', brand: 'Unsubscribed from this brand', campaign: 'Unsubscribed from this campaign' } as const;
const CAP_SUFFIX: Record<string, string> = { daily_cap: ' — daily limit', followup_daily_cap: ' — follow-up limit' };
const STATUS_DETAIL: Partial<Record<OutreachRow['status'], string>> = {
  accepted_by_admin: 'Accepted',
  accepted_by_creator: 'Accepted by creator',
  rejected_by_brand: 'Brand declined',
  rejected_by_creator: 'Creator declined',
};

/** A sourced creator whose first touch is on its way or went out reads as Contacted (the Contacted tab holds it too). A failed one sent no mail: the row stays Not contacted and says why (R16-5). */
const STARTED_FIRST_TOUCH: ReadonlySet<string> = new Set(['queued', 'sending', 'sent', 'unknown_delivery']);
function startedFirstTouch(row: OutreachRow): OutreachMessageStatus | null {
  const status = row.firstTouch?.status;
  if (row.status !== 'outreach' || row.outreachState !== 'sourced' || !status) return null;
  return STARTED_FIRST_TOUCH.has(status) ? (status as OutreachMessageStatus) : null;
}

const ATTENTION_STATUSES: ReadonlySet<string> = new Set(['held', 'failed', 'unknown_delivery']);

const isFailedFirstTouch = (row: OutreachRow) => row.status === 'outreach' && row.outreachState === 'sourced' && row.firstTouch?.status === 'failed';

/** T6: a sourced creator whose first touch waits for approval reads as a draft ready, whether or not lastMessage carries it. A held, failed or unconfirmed last message keeps its own label. */
function draftReadyFirstTouch(row: OutreachRow): OutreachMessageStatus | null {
  if (row.lastMessage && ATTENTION_STATUSES.has(row.lastMessage.status)) return null;
  return row.status === 'outreach' && row.outreachState === 'sourced' && row.firstTouch?.status === 'awaiting_approval' ? 'awaiting_approval' : null;
}

const tzOf = (zone: string | undefined, date: Date) => (zone ? zoneAbbrev(zone, date) : 'UTC');

function whenText(date: Date, zone: string | undefined, now: Date, alwaysDate: boolean): string {
  const day = !alwaysDate && zoneDayKey(date, zone) === zoneDayKey(now, zone) ? '' : `${shortDate(date, zone)}, `;
  return `${day}${shortTime(date, zone)} ${tzOf(zone, date)}`;
}

/** A state this mapping does not list is a compile error here, and at run time reads as unknown rather than as Stopped. */
function unlistedState(_state: never): RowPill {
  return { label: UNKNOWN_PILL_LABEL, tone: 'neutral' };
}

function pillOf(row: OutreachRow, started: OutreachMessageStatus | null): RowPill {
  if (row.status === 'outreach') {
    switch (row.outreachState) {
      case 'sourced':
        return started ? { label: CONTACTED_PILL_LABEL, tone: 'brand' } : { label: 'Not contacted', tone: 'neutral' };
      case 'contacted':
        return { label: CONTACTED_PILL_LABEL, tone: 'brand' };
      case 'replied':
        return { label: 'Replied', tone: 'replied' };
      case 'rate_provided':
        return { label: 'Rate agreed', tone: 'good' };
      case 'removed':
        return { label: 'Removed', tone: 'removed' };
      case 'unsubscribed':
        return { label: 'Stopped', tone: 'stopped', hint: row.suppressionScope ? SCOPE_TITLES[row.suppressionScope] : UNSUBSCRIBED_REASON };
      case 'rejected':
      case 'not_interested':
      case 'ghosted':
      case 'bounced':
        return { label: 'Stopped', tone: 'stopped', hint: STOPPED_REASONS[row.outreachState], stopReason: STOPPED_REASONS[row.outreachState] };
      default:
        return unlistedState(row.outreachState);
    }
  }
  if (row.status === 'removed_by_admin' || row.status === 'rejected_by_admin') return { label: 'Removed', tone: 'removed' };
  return { label: 'With brand', tone: 'good' };
}

/** What the pill alone does not say, for the states whose detail outranks every other line. Null: the pill is enough. */
function stateDetail(row: OutreachRow): string | null {
  if (row.status === 'outreach') return null;
  if (row.status === 'rejected_by_admin') return 'Rejected';
  if (row.status === 'removed_by_admin' || !isWithBrandStatus(row.status)) return null;
  switch (row.brandState ?? 'none') {
    case 'approved':
      if (row.status !== 'accepted_by_brand') return 'Approved';
      return isCreatorTold(row) ? 'Approved, creator told' : 'Approved, tell the creator';
    case 'counter_requested':
      return 'Asked for less';
    case 'rejected':
      return 'Brand declined';
    case 'pending':
      return 'Pending';
    default:
      return STATUS_DETAIL[row.status] ?? null;
  }
}

function failedStepTitle(f: OutreachRow['followup']): string | undefined {
  if (f?.kind !== 'steps_remaining' || f.state !== 'failed') return undefined;
  const attempts = f.attempts ? `${f.attempts} ${f.attempts === 1 ? 'attempt' : 'attempts'}` : '';
  return [f.reason, attempts].filter(Boolean).join(' · ') || undefined;
}

function sendLine(row: OutreachRow, status: OutreachMessageStatus | undefined, first: boolean, ctx: Required<RowLabelContext>): string | null {
  const lead = first ? 'First email' : '';
  const ft = row.firstTouch;
  if (first && ft?.status === 'scheduled') {
    const at = ft.scheduledFor ?? row.nextSendAt;
    const date = at ? new Date(at) : null;
    if (!date || Number.isNaN(date.getTime())) return 'First email is scheduled';
    return `First email sends ${whenText(date, ctx.campaignZone ?? row.nextSendZone, ctx.now, true)}`;
  }
  if (status === 'sending') return first ? 'First email sending now' : 'Sending now';
  if (status !== 'queued') return null;
  const date = row.nextSendAt ? new Date(row.nextSendAt) : null;
  if (date && date.getTime() > ctx.now.getTime()) {
    const zone = first ? (ctx.campaignZone ?? row.nextSendZone) : (row.nextSendZone ?? ctx.campaignZone);
    const reason = row.heldReason ?? row.lastMessage?.heldReason ?? '';
    const cap = Object.hasOwn(CAP_SUFFIX, reason) ? CAP_SUFFIX[reason] : '';
    return `${lead ? `${lead} sends` : 'Sends'} ${whenText(date, zone, ctx.now, first)}${cap}`;
  }
  return row.holdReason?.label ?? `${lead ? `${lead} queued` : 'Queued'}, sends shortly`;
}

function holdLine(row: OutreachRow): string {
  const code = row.lastMessage?.heldReason ?? row.holdReason?.code;
  return row.holdReason?.label ?? (code ? `Held — ${heldReasonLabel(code)}` : 'Held');
}

/** A projection-lagged row's labels, or null when the row is up to date. A stuck sync will not clear by waiting, so it
 *  says so and keeps Do not contact, which is read apart from the projection. */
function laggingLabels(row: OutreachRow): RowLabels | null {
  if (row.syncStuck) {
    return { pill: { label: SYNC_STUCK_PILL_LABEL, tone: 'stuck', hint: SYNC_STUCK_HINT }, badge: null, doNotContact: doNotContactBadge(row), line: null };
  }
  if (row.syncing) return { pill: { label: SYNCING_PILL_LABEL, tone: 'neutral' }, badge: null, doNotContact: null, line: null };
  return null;
}

export function rowLabels(row: OutreachRow, input: RowLabelContext = {}): RowLabels {
  const lagging = laggingLabels(row);
  if (lagging) return lagging;
  const ctx = { campaignZone: input.campaignZone, autoFollowups: input.autoFollowups ?? true, now: input.now ?? new Date() } as Required<RowLabelContext>;
  const started = startedFirstTouch(row);
  const pill = pillOf(row, started);
  const active = row.status === 'outreach' && ACTIVE_STATES.has(row.outreachState);
  const status = started ?? draftReadyFirstTouch(row) ?? (isFailedFirstTouch(row) ? 'failed' : row.lastMessage?.status);
  const followup = active ? followupLabel(row, ctx.now, ctx.autoFollowups, true) : null;
  const holdCode = row.lastMessage?.heldReason ?? row.holdReason?.code;

  return { pill, badge: badgeOf(row, active, status, holdCode, followup), doNotContact: doNotContactBadge(row), line: lineOf(row, pill, active, status, started, followup, ctx) };
}

/** G8-11: the creator is suppressed (here or in another campaign), so every entry of theirs says so, beside whatever other badge the row has; the state itself is untouched. */
function doNotContactBadge(row: OutreachRow): RowBadge | null {
  if (!row.suppressed || row.status === 'removed_by_admin' || row.status === 'rejected_by_admin') return null;
  if (row.status === 'outreach' && row.outreachState === 'removed') return null;
  return { label: 'Do not contact', hint: row.suppressionScope ? `Do not contact — ${SCOPE_WORDS[row.suppressionScope]}` : undefined };
}

function badgeOf(row: OutreachRow, active: boolean, status: OutreachMessageStatus | undefined, holdCode: string | undefined, followup: ReturnType<typeof followupLabel>): RowBadge | null {
  if (row.escalatedAt) {
    return { label: row.escalationReason ? `Needs you · ${escalationLabel(row.escalationReason)}` : 'Needs you', hint: escalationTitle(row) };
  }
  if (active) {
    if (status === 'failed') return { label: 'Send failed', hint: row.lastMessage?.lastError };
    if (status === 'unknown_delivery') return { label: 'Delivery unconfirmed', hint: row.lastMessage?.lastError };
    if (status === 'held' && holdCode !== 'suppressed') return { label: 'Held', hint: row.holdReason?.label };
  }
  if (row.awaitingReply) return { label: AWAITING_REPLY_LABEL, hint: AWAITING_REPLY_HINT };
  if (!active) return null;
  if (followup?.tone === 'due' || followup?.tone === 'overdue') return { label: 'Follow up now' };
  if (followup?.tone === 'failed') return { label: 'Follow-up failed', hint: failedStepTitle(row.followup) };
  return null;
}

function lineOf(
  row: OutreachRow,
  pill: RowPill,
  active: boolean,
  status: OutreachMessageStatus | undefined,
  started: OutreachMessageStatus | null,
  followup: ReturnType<typeof followupLabel>,
  ctx: Required<RowLabelContext>,
): RowLine | null {
  const detail = stateDetail(row);
  if (!active) return detail ? { text: detail } : null;

  if (row.suppressed) {
    return { text: row.suppressionScope ? `Do not contact — ${SCOPE_WORDS[row.suppressionScope]}` : 'Do not contact' };
  }
  if (status === 'held') return { text: holdLine(row) };
  if (isFailedFirstTouch(row)) {
    const reason = row.lastMessage?.lastError;
    return { text: reason ? `Not sent · ${reason}` : 'Not sent' };
  }
  if (status === 'awaiting_approval' && row.draftWaitingEmail) {
    return isApprovedWaiting(row, ctx.now) && row.waitingSend
      ? { text: SCHEDULED_WAITING_LABEL, hint: waitingSendHint(row.waitingSend, ctx.campaignZone, ctx.now) }
      : { text: 'Draft saved, waiting for an email', hint: 'Approve it and it goes out once an email is added.' };
  }
  const sending = sendLine(row, status, row.outreachState === 'sourced' || started != null, ctx);
  if (sending) return { text: sending };
  if (status === 'awaiting_approval') return { text: 'Draft ready to review' };
  if (row.followupsPausedAt && followupPauseAction(row) === 'resume') return { text: 'Follow-ups paused' };
  if (followup && followup.tone !== 'failed') {
    return { text: followup.text, hint: row.followup?.kind === 'creator_wait' ? row.followupDeferred?.reason : undefined };
  }
  const deferred = followupDeferredLabel(row);
  if (deferred) return { text: deferred, hint: row.followupDeferred?.reason };
  if (isSequenceEnded(row)) return { text: SEQUENCE_ENDED_LABEL };
  if (pill.label === 'Replied') {
    const intent = row.openQuoteIntent ?? row.lastReply?.intent;
    return intent && intent !== 'auto_reply' && intent !== 'unsubscribe' ? { text: INTENT_LABELS[intent] } : null;
  }
  if (pill.label === 'Rate agreed') {
    const amount = agreedAmountLabel(row);
    return amount ? { text: `${amount} agreed` } : null;
  }
  return null;
}
